import { MAP_DEFAULT_CENTER } from '../../config';
import { routingJobsApi, RoutingJobResponse, RoutingJobViewResponse } from '../../api/routingJobsApi';
import {
  TaskConfig,
  TaskInfo,
  Trip,
  TouchPoint,
  EditPerformance,
  BreachesAndRatios,
  TripCostPerformance,
} from '../../models/types';
import { DriverInfo, OrderBundle, VerifyPayload, VehicleInfo } from '../../api/wmService';

type AnyRecord = Record<string, any>;

const DEFAULT_EDIT_PERFORMANCE: EditPerformance = {
  editPercent: 0,
  tpPerTrip: 0,
  wtPerTripKg: 0,
  distPerTripKm: 0,
  tpsWithoutTrip: 0,
  noOfTrips: 0,
  cpk: 0,
  cpTp: 0,
  tripsWithTimeBreach: 0,
  noOfSlotSlippage: 0,
  tpPerTripDelta: 0,
  wtPerTripKgDelta: 0,
  distPerTripKmDelta: 0,
  tpsWithoutTripDelta: 0,
  noOfTripsDelta: 0,
  cpkDelta: 0,
  cpTpDelta: 0,
  tripsWithTimeBreachDelta: 0,
  noOfSlotSlippageDelta: 0,
  ctoGmv: 0,
  ctoGmvDelta: 0,
};

interface OriginShape {
  id: string;
  name: string;
  lat: number;
  lng: number;
  maxTp: number;
  maxKm: number;
}

export function adaptGembaTaskInfo(
  depotId: string,
  jobId: string,
  summaryView: RoutingJobViewResponse,
  tripsView: RoutingJobViewResponse,
  supportViews?: Record<string, RoutingJobViewResponse | undefined>
): TaskInfo {
  const summary = summaryView.summary ?? {};
  const editPerformance = normalizeEditPerformance(summary.editPerformance);
  const vehicleMetaByPlate = buildVehicleMetaByPlate(supportViews?.verify_vehicles?.items ?? []);
  const trips = (tripsView.items ?? []).map((item) => mapTrip(item, vehicleMetaByPlate));
  const config = buildTaskConfig(depotId, summary, trips);

  return {
    taskId: jobId,
    userId: summary.createdBy ?? '',
    tenant: summary.tenantId ?? 'gemba',
    status: summary.status ?? 'COMPLETED',
    config,
    editPerf: editPerformance,
    trips,
  };
}

export async function fetchGembaVerifyPayload(depotId: string, jobId: string): Promise<VerifyPayload> {
  const workspace = await routingJobsApi.getVerifyWorkspace(depotId, jobId);
  const views = Object.fromEntries((workspace.views ?? []).map((view) => [view.viewId, view]));
  return adaptGembaVerifyPayload(workspace.job, views);
}

export function adaptGembaVerifyPayload(
  job: RoutingJobResponse,
  views: Record<string, RoutingJobViewResponse | undefined>
): VerifyPayload {
  const ordersView = views.verify_orders;
  const vehiclesView = views.verify_vehicles;
  const depotView = views.verify_depot;

  const origin = deriveOrigin(job, depotView?.items ?? []);
  const orders = mapGembaOrdersFromViewWithOrigin(ordersView?.items ?? [], origin);
  const drivers = mapDriversFromVehicles(vehiclesView?.items ?? []);
  const fleet = mapFleetFromVehicles(vehiclesView?.items ?? []);

  const dropLookups: Record<string, { dropId: string; address: string; lat: number; lng: number; hourStart: string; hourEnd: string }> = {};
  orders.forEach((order) => {
    dropLookups[order.drop.id] = {
      dropId: order.drop.id,
      address: order.drop.address,
      lat: order.drop.lat,
      lng: order.drop.lng,
      hourStart: order.fromTime || '00:00',
      hourEnd: order.toTime || '24:00',
    };
  });
  const originId = origin.id;
  const pickupId = `${originId}-DEPOT`;

  return {
    runId: job.id,
    origin: {
      originId,
      name: origin.name,
      lat: origin.lat,
      lng: origin.lng,
      zip: '',
    },
    orders,
    drivers,
    fleet,
    equipment: [],
    lookups: {
      origin: {
        originId,
        address: `${origin.name} | MaxTP: ${origin.maxTp} | MaxKM: ${origin.maxKm}`,
        zip: '',
        lat: origin.lat,
        lng: origin.lng,
        hourStart: '00:00',
        hourEnd: '24:00',
      },
      clients: {
        [pickupId]: {
          clientId: pickupId,
          address: origin.name,
          lat: origin.lat,
          lng: origin.lng,
          hourStart: '00:00',
          hourEnd: '24:00',
        },
      },
      drops: dropLookups,
    },
  };
}

export function mapGembaOrdersFromView(items: AnyRecord[]): OrderBundle[] {
  return mapGembaOrdersFromViewWithOrigin(items, {
    id: 'gemba-origin',
    name: 'Depot',
    lat: MAP_DEFAULT_CENTER[1],
    lng: MAP_DEFAULT_CENTER[0],
    maxTp: 0,
    maxKm: 0,
  });
}

function mapTrip(item: AnyRecord, vehicleMetaByPlate: Map<string, VehicleTripMeta>): Trip {
  const geoMarkers = item.geoMarkers ?? {};
  const rawOrders = Array.isArray(item.orders) ? item.orders : [];
  const tpDetails = rawOrders.map(mapOrderTouchPoint);
  const breaches = fillBreaches(item.breachesAndRatios);
  const tripCostPerf = fillCostPerformance(item.tripCostPerf);
  const tripIndex = String(item.tripIndex ?? item.tripId ?? '0');
  const plate = String(item.licensePlate ?? '').trim();
  const vehicleMeta = vehicleMetaByPlate.get(normalizePlateKey(plate));
  const capacityKg = Math.abs(toNumber(item.capacityKg ?? vehicleMeta?.capacityKg));
  const workingTime = String(item.workingTime ?? vehicleMeta?.workingTime ?? '').trim();
  const workingMinutes = getWorkingWindowMinutes(workingTime);
  const tripDurationMinutes = parseDurationToMinutes(item.travelTime);
  const tripStartMinutes = timeToMinutes(item.tripStartTime);

  return {
    name: `Trip-${tripIndex}`,
    vehTripNo: toNumber(item.tripIndex ?? item.tripId),
    distKm: toNumber(item.distKm),
    totalWtKg: toNumber(item.weightKg),
    volCuFt: 0,
    slotTags: [],
    geoMarkers: {
      tripBoundaryLngLats: geoMarkers.tripBoundaryLngLats ?? [],
      tripCenterLngLat: asLngLat(geoMarkers.tripCenterLngLat) ?? MAP_DEFAULT_CENTER,
      tripPolylineLngLats: asLngLatArray(geoMarkers.tripPolylineLngLats),
    },
    breachesAndRatios: breaches,
    tripCostPerf,
    vehDetails: {
      vehId: plate,
      driverName: '',
      contractId: '',
      contractType: '',
      capacityKg: capacityKg > 0 ? capacityKg : undefined,
      workingTime: workingTime || undefined,
      workingMinutes: workingMinutes != null && workingMinutes > 0 ? workingMinutes : undefined,
    },
    tpDetails,
    sortValues: normalizeSortValues(item.sortValues),
    assignmentMeta: {
      tripDurationMinutes: tripDurationMinutes != null && tripDurationMinutes > 0 ? tripDurationMinutes : undefined,
      originStartMinutes: tripStartMinutes > 0 ? tripStartMinutes : undefined,
    },
  };
}

function mapOrderTouchPoint(item: AnyRecord): TouchPoint {
  const lng = toNumber(item.dropLongitude);
  const lat = toNumber(item.dropLatitude);
  const window = buildOpenHours(item.timeWindowStart, item.timeWindowEnd);
  const slotTag = item.slotTag ? [String(item.slotTag)] : [];
  const orderId = String(item.orderId ?? '');
  const explicitDuration = toNumber(item.deliveryDurationMinutes ?? item.transactionTime);

  return {
    tpId: orderId,
    seqNo: toNumber(item.sequence),
    name: orderId,
    address: String(item.address ?? item.location ?? orderId),
    zipCode: '',
    lngLat: [lng, lat],
    attemptTime: toAttemptTime(item.deliveredAt, item.deliveryEndsAt),
    openHoursMinOfDay: window,
    slotTags: slotTag,
    timeBreachMin: toNumber(item.timeBreachMinutes),
    itemSummary: {
      dropWtKg: toNumber(item.dropWeightKG),
      pickWtKg: 0,
      volCuFt: 0,
      gmv: 0,
      dropItemCount: 1,
      pickItemCount: 0,
      distinctItemTags: [],
    },
    orderNumber: orderId,
    deliveryDurationMinutes: explicitDuration > 0 ? explicitDuration : undefined,
    timeWindowRaw: String(item.timeWindowRaw ?? ''),
    geoCorrectionRef: orderId,
    geoCorrectionRole: 'DROP',
    geoCorrectionSupported: true,
  };
}

function mapGembaOrdersFromViewWithOrigin(items: AnyRecord[], origin: OriginShape): OrderBundle[] {
  const pickupId = `${origin.id}-DEPOT`;
  return items.map((item, index) => {
    const soId = String(item.orderId ?? `ORDER-${index + 1}`);
    const window = parseWindow(item.timeWindow);
    const weight = Math.abs(toNumber(item.weight));
    const lat = isFiniteNumber(item.latitude) ? Number(item.latitude) : origin.lat;
    const lng = isFiniteNumber(item.longitude) ? Number(item.longitude) : origin.lng;
    const address = String(item.address ?? item.location ?? soId);
    return {
      soId,
      fromTime: window.start,
      toTime: window.end,
      wasteType: String(item.orderType ?? ''),
      qty: weight,
      uom: 'kg',
      deliveryDurationMinutes: toNumber(item.deliveryDurationMinutes ?? item.transactionTime),
      hazMat: 'No',
      requiredAsset: '',
      pickup: {
        id: pickupId,
        lat: origin.lat,
        lng: origin.lng,
        address: origin.name,
      },
      drop: {
        id: soId,
        lat,
        lng,
        address,
      },
      pickupGeoCorrection: {
        ref: pickupId,
        role: 'ORIGIN',
        editable: false,
      },
      dropGeoCorrection: {
        ref: soId,
        role: 'DROP',
        editable: true,
      },
    };
  });
}

function mapDriversFromVehicles(items: AnyRecord[]): DriverInfo[] {
  return items.map((item, index) => {
    const plate = String(item.licensePlate ?? `VEH-${index + 1}`);
    const shift = parseWorkingWindow(item.workingTime);
    const capacity = Math.abs(toNumber(item.capacityKg));
    return {
      driverId: plate,
      name: plate,
      class: capacity > 0 ? `Capacity ${capacity}kg` : 'Vehicle',
      available: true,
      vehicleNumber: plate,
      vehicleCapacityKg: capacity,
      shiftStartTime: shift.start,
      shiftEndTime: shift.end,
      allowedVehicleTypes: ['TRUCK'],
      licenceClasses: [],
      skillTags: [],
    };
  });
}

function mapFleetFromVehicles(items: AnyRecord[]): VehicleInfo[] {
  return items.map((item, index) => {
    const plate = String(item.licensePlate ?? `VEH-${index + 1}`);
    const capacity = Math.abs(toNumber(item.capacityKg));
    return {
      vehicleId: plate,
      name: plate,
      capacity,
      uom: 'kg',
      itemType: 'delivery',
      licenseRequired: '',
      assetType: 'TRUCK',
      tractorNeeded: 'No',
    };
  });
}

function deriveOrigin(job: AnyRecord, depotItems: AnyRecord[]): OriginShape {
  const depot = depotItems?.[0] ?? {};
  const lat = isFiniteNumber(depot.sourceLatitude) ? Number(depot.sourceLatitude) : MAP_DEFAULT_CENTER[1];
  const lng = isFiniteNumber(depot.sourceLongitude) ? Number(depot.sourceLongitude) : MAP_DEFAULT_CENTER[0];
  return {
    id: job?.depotId ?? 'gemba-depot',
    name: job?.depotSnapshot?.name ?? 'GEMBA Depot',
    lat,
    lng,
    maxTp: toNumber(depot.maxTp),
    maxKm: toNumber(depot.maxKm),
  };
}

function buildTaskConfig(depotId: string, summary: AnyRecord, trips: Trip[]): TaskConfig {
  const sourceLocation = summary?.settings?.sourceLocation;
  const center: [number, number] =
    Array.isArray(sourceLocation) && sourceLocation.length === 2
      ? [toNumber(sourceLocation[0]), toNumber(sourceLocation[1])]
      : findMapCenter(trips);
  return {
    id: depotId,
    algoType: summary.modelType ?? 'GEMBA_SLOTTED',
    sourceLngLat: center,
    geoBarriers: [],
  };
}

function findMapCenter(trips: Trip[]): [number, number] {
  for (const trip of trips) {
    const center = trip.geoMarkers?.tripCenterLngLat;
    if (Array.isArray(center) && center.length === 2) {
      return [toNumber(center[0]), toNumber(center[1])];
    }
  }
  return MAP_DEFAULT_CENTER;
}

function normalizeEditPerformance(raw: AnyRecord | undefined): EditPerformance {
  if (!raw) return DEFAULT_EDIT_PERFORMANCE;
  const units = typeof raw.units === 'object' ? raw.units : undefined;
  return {
    editPercent: toNumber(raw.editPercent),
    tpPerTrip: toNumber(raw.tpPerTrip),
    wtPerTripKg: toNumber(raw.wtPerTripKg),
    distPerTripKm: toNumber(raw.distPerTripKm),
    tpsWithoutTrip: toNumber(raw.tpsWithoutTrip),
    noOfTrips: toNumber(raw.noOfTrips),
    cpk: toNumber(raw.cpk),
    cpTp: toNumber(raw.cpTp),
    tripsWithTimeBreach: toNumber(raw.tripsWithTimeBreach),
    noOfSlotSlippage: toNumber(raw.noOfSlotSlippage),
    tpPerTripDelta: toNumber(raw.tpPerTripDelta),
    wtPerTripKgDelta: toNumber(raw.wtPerTripKgDelta),
    distPerTripKmDelta: toNumber(raw.distPerTripKmDelta),
    tpsWithoutTripDelta: toNumber(raw.tpsWithoutTripDelta),
    noOfTripsDelta: toNumber(raw.noOfTripsDelta),
    cpkDelta: toNumber(raw.cpkDelta),
    cpTpDelta: toNumber(raw.cpTpDelta),
    tripsWithTimeBreachDelta: toNumber(raw.tripsWithTimeBreachDelta),
    noOfSlotSlippageDelta: toNumber(raw.noOfSlotSlippageDelta),
    ctoGmv: toNumber(raw.ctoGmv),
    ctoGmvDelta: toNumber(raw.ctoGmvDelta),
    units,
  };
}

function fillBreaches(raw: AnyRecord | undefined): BreachesAndRatios {
  const source = raw ?? {};
  return {
    wtBreachKg: toNumber(source.wtBreachKg),
    volBreachCuFt: toNumber(source.volBreachCuFt),
    distBreachKm: toNumber(source.distBreachKm),
    tpBreach: toNumber(source.tpBreach),
    timeBreachMin: toNumber(source.timeBreachMin),
    slotBreaches: toNumber(source.slotBreaches),
    wtToLimit: toNumber(source.wtToLimit),
    volToLimit: toNumber(source.volToLimit),
    distToLimit: toNumber(source.distToLimit),
    tpToLimit: toNumber(source.tpToLimit),
    timeToLimit: toNumber(source.timeToLimit),
    distinctZipCodes: source.distinctZipCodes ?? [],
  };
}

function fillCostPerformance(raw: AnyRecord | undefined): TripCostPerformance {
  const source = raw ?? {};
  return {
    cost: toNumber(source.cost),
    cpk: toNumber(source.cpk),
    cpt: toNumber(source.cpt),
    gmv: toNumber(source.gmv),
    ctoGmv: toNumber(source.ctoGmv),
  };
}

function normalizeSortValues(raw: AnyRecord | undefined): Record<string, number | string> | undefined {
  if (!raw) return undefined;
  const normalized: Record<string, number | string> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (typeof value === 'number') {
      normalized[key] = value;
      return;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      normalized[key] = Number.isFinite(parsed) ? parsed : value;
      return;
    }
    if (typeof value === 'boolean') {
      normalized[key] = value ? 1 : 0;
    }
  });
  return normalized;
}

function parseWindow(raw: any): { start: string; end: string } {
  const value = String(raw ?? '').trim();
  if (!value) {
    return { start: '00:00', end: '24:00' };
  }
  const parts = value.split('-').map((v) => normalizeTime(v));
  if (parts.length >= 2) {
    return { start: parts[0], end: parts[1] };
  }
  const fallback = normalizeTime(value);
  return { start: fallback, end: fallback };
}

function parseWorkingWindow(raw: any): { start: string; end: string } {
  const value = String(raw ?? '').trim();
  if (!value) return { start: '00:00', end: '24:00' };
  const pieces = value.split('-').map((v) => normalizeTime(v));
  if (pieces.length >= 2) {
    return { start: pieces[0], end: pieces[1] };
  }
  return { start: '00:00', end: '24:00' };
}

interface VehicleTripMeta {
  capacityKg: number;
  workingTime: string;
}

function buildVehicleMetaByPlate(items: AnyRecord[]): Map<string, VehicleTripMeta> {
  const lookup = new Map<string, VehicleTripMeta>();
  items.forEach((item) => {
    const plate = normalizePlateKey(item.licensePlate);
    if (!plate) return;
    lookup.set(plate, {
      capacityKg: Math.abs(toNumber(item.capacityKg)),
      workingTime: String(item.workingTime ?? '').trim(),
    });
  });
  return lookup;
}

function normalizePlateKey(raw: unknown): string {
  return String(raw ?? '').trim().toUpperCase();
}

function getWorkingWindowMinutes(raw: string): number | null {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length < 2) return null;
  const start = timeToMinutes(parts[0]);
  const end = timeToMinutes(parts[1]);
  const normalizedEnd = end >= start ? end : end + 1440;
  return Math.max(0, normalizedEnd - start);
}

function parseDurationToMinutes(raw: unknown): number | null {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  const parts = value.split(':').map((part) => Number(part));
  if (parts.length === 2 && parts.every(Number.isFinite)) {
    return (parts[0] * 60) + parts[1];
  }
  if (parts.length === 3 && parts.every(Number.isFinite)) {
    return (parts[0] * 60) + parts[1] + Math.round(parts[2] / 60);
  }
  return null;
}

function toAttemptTime(startRaw?: string, endRaw?: string): { start: number; end: number } {
  return {
    start: timeToMinutes(startRaw),
    end: timeToMinutes(endRaw),
  };
}

function buildOpenHours(startRaw?: string, endRaw?: string): { start: number; end: number }[] {
  const start = timeToMinutes(startRaw);
  const end = timeToMinutes(endRaw);
  if (!startRaw && !endRaw) {
    return [{ start: 0, end: 1440 }];
  }
  return [{ start, end: end === 0 && String(endRaw ?? '').includes('24') ? 1440 : end }];
}

function normalizeTime(raw: string): string {
  const minutes = timeToMinutes(raw);
  if (String(raw).includes('24:00')) return '24:00';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeToMinutes(raw?: string): number {
  if (!raw) return 0;
  const value = String(raw).trim().toUpperCase();
  if (!value) return 0;
  if (value === '24:00') return 1440;
  const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/);
  if (!match) return 0;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const ampm = match[4];
  if (ampm === 'AM' && hours === 12) {
    hours = 0;
  } else if (ampm === 'PM' && hours !== 12) {
    hours += 12;
  }
  return hours * 60 + minutes;
}

function asLngLat(value: any): [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  return [toNumber(value[0]), toNumber(value[1])];
}

function asLngLatArray(value: any): [number, number][] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value
    .filter((entry) => Array.isArray(entry) && entry.length >= 2)
    .map((entry) => [toNumber(entry[0]), toNumber(entry[1])] as [number, number]);
  return rows.length ? rows : undefined;
}

function isFiniteNumber(value: any): boolean {
  return Number.isFinite(Number(value));
}

function toNumber(value: any): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
